import { ShieldAlert, Eye, DollarSign, BarChart3 } from 'lucide-react';
import { useInView } from '../hooks/useInView';
import type { ReactNode } from 'react';

interface StatCard {
  icon: ReactNode;
  stat: string;
  label: string;
  description: string;
}

const stats: StatCard[] = [
  {
    icon: <ShieldAlert className="h-8 w-8 text-red-400" aria-hidden="true" />,
    stat: '73%',
    label: 'of enterprises',
    description:
      'have ungoverned AI usage across departments with no centralized policy enforcement.',
  },
  {
    icon: <Eye className="h-8 w-8 text-amber-400" aria-hidden="true" />,
    stat: '40%',
    label: 'of prompts',
    description:
      'contain sensitive data — PII, credentials, or proprietary code — sent to third-party AI models.',
  },
  {
    icon: <DollarSign className="h-8 w-8 text-emerald-400" aria-hidden="true" />,
    stat: '$2.4M',
    label: 'average AI cost overrun',
    description:
      'per year due to uncontrolled model usage, redundant API calls, and lack of budget allocation.',
  },
  {
    icon: <BarChart3 className="h-8 w-8 text-indigo-400" aria-hidden="true" />,
    stat: 'Zero visibility',
    label: 'into AI model usage',
    description:
      "across teams. Most organizations can't answer: who is using which models, for what, and at what cost.",
  },
];

export function ProblemSection() {
  const [ref, isInView] = useInView();

  return (
    <section
      id="problem"
      className="relative py-24 sm:py-32 bg-gray-950"
      aria-labelledby="problem-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2
            id="problem-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
          >
            Shadow AI is your <span className="text-red-400">biggest risk</span>
          </h2>
          <p className="mt-4 text-lg text-gray-400">
            Enterprises are adopting AI faster than security teams can govern it. Without a
            centralized governance layer, every AI interaction is a potential compliance violation,
            data leak, or cost overrun.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((card, i) => (
            <StatCardComponent key={card.stat} card={card} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatCardComponent({ card, index }: { card: StatCard; index: number }) {
  const [ref, isInView] = useInView();

  return (
    <div
      ref={ref}
      className={`group relative rounded-2xl border border-gray-800 bg-gray-900/50 p-6 hover:border-gray-700 transition-all duration-700 ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="mb-4">{card.icon}</div>
      <div className="text-3xl font-bold text-white">{card.stat}</div>
      <div className="text-sm font-medium text-gray-400 mt-1">{card.label}</div>
      <p className="mt-3 text-sm text-gray-500 leading-relaxed">{card.description}</p>
    </div>
  );
}
